/**
 * "Recommendations" tab on the Deal detail page.
 * Renders an always-visible filter row (date range + applied-by user),
 * followed by an infinite-scroll list of applied recommendation events,
 * with loading skeletons, an error banner, and an empty state.
 */
import { useState } from 'react'
import { Alert } from 'components/FbUI/Alert'
import { Skeleton } from 'components/FbUI/Skeleton'
import { TeamMemberFilter } from 'components/NewCompanyPage/EventHistory/filters/TeamMemberFilter'
import { useDealRecommendationHistory } from './useDealRecommendationHistory'
import { DateRangePicker } from './DateRangePicker'
import type { DateRangeValue } from './DateRangePicker'
import { RecommendationEventCard } from './RecommendationEventCard'

export function DealRecommendationsTab({ dealId }: { dealId: number }) {
  const [dateRange, setDateRange] = useState<DateRangeValue>(undefined)
  const [userIds, setUserIds] = useState<number[]>([])

  const {
    items,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    error,
  } = useDealRecommendationHistory(dealId, { dateRange, userIds })

  const hasActiveFilter = !!dateRange || userIds.length > 0

  return (
    <div>
      {/* Filter row — always rendered so users can clear filters even in
          loading / error / empty states. Mirrors DealEventHistory's sticky
          filter row layout and z-index. */}
      <div className="sticky top-0 z-[19] bg-surface pt-1.5 pb-2 flex flex-col gap-2">
        <div className="flex items-center gap-2">
          <DateRangePicker value={dateRange} onChange={setDateRange} />
          <TeamMemberFilter value={userIds} onChange={setUserIds} />
        </div>
      </div>

      {/* Body: loading | error | empty | list + Load more */}
      {isLoading ? (
        <div className="flex flex-col gap-3 py-2">
          {[0, 1, 2].map((i) => (
            <div key={i} className="flex items-start gap-3">
              <Skeleton className="h-9 w-9 flex-shrink-0 rounded-full" />
              <div className="flex-1 space-y-2 rounded-lg bg-white px-3 py-3 shadow-sm">
                <Skeleton className="h-4 w-1/3" />
                <Skeleton className="h-4 w-2/3" />
              </div>
            </div>
          ))}
        </div>
      ) : error ? (
        <div className="py-4">
          <Alert color="danger" description="Failed to load recommendations." />
        </div>
      ) : items.length === 0 ? (
        <div className="min-h-[100px] py-6 text-center text-gray-400">
          {hasActiveFilter
            ? 'No recommendations match these filters.'
            : 'No recommendation actions on this deal yet.'}
        </div>
      ) : (
        <div className="flex flex-col gap-3 py-2">
          {items.map((item) => (
            <RecommendationEventCard key={item.id} item={item} />
          ))}

          {hasNextPage && (
            <button
              type="button"
              onClick={() => fetchNextPage()}
              disabled={isFetchingNextPage}
              className="self-center text-base text-brand-active font-medium disabled:opacity-50"
            >
              {isFetchingNextPage ? 'Loading…' : 'Load more'}
            </button>
          )}
        </div>
      )}
    </div>
  )
}
